import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useCommunityMember } from '@/contexts/CommunityMemberContext';
import { toast } from '@/components/ui/use-toast';

export type Review = {
  id: string;
  business_id: string;
  user_id: string;
  rating: number;
  description: string;
  status: 'Pending Validation' | 'Published' | 'Rejected';
  created_at: string;
  updated_at: string;
  display_name: string | null;
  authorName?: string;
  authorImage?: string;
  reply?: any;
};

export const useReviews = (businessId?: string) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { isAllowedTo } = useCommunityMember();

  const isBusinessOwnerQuery = useQuery({
    queryKey: ['isBusinessOwner', businessId, user?.id],
    queryFn: async () => {
      if (!businessId || !user) return false;

      try {
        const { data, error } = await supabase
          .from('businesses')
          .select('user_id')
          .eq('id', businessId)
          .single();

        if (error) {
          return false;
        }

        const isOwner = data.user_id === user.id;
        return isOwner;
      } catch (err) {
        return false;
      }
    },
    enabled: !!businessId && !!user,
  });

  const canViewUnpublishedReviewsQuery = useQuery({
    queryKey: ['canViewUnpublishedReviews', user?.id],
    queryFn: async () => {
      if (!user) return false;
      
      try {
        return await isAllowedTo('view_all_reviews');
      } catch (err) {
        console.error('Error checking review viewing permission:', err);
        return false;
      }
    },
    enabled: !!user,
  });

  const canManageAllReviewsQuery = useQuery({
    queryKey: ['canManageAllReviews', user?.id],
    queryFn: async () => {
      if (!user) return false;
      
      try {
        return await isAllowedTo('manage_all_reviews');
      } catch (err) {
        console.error('Error checking review management permission:', err);
        return false;
      }
    },
    enabled: !!user,
  });

  const reviewsQuery = useQuery({
    queryKey: ['reviews', businessId],
    queryFn: async () => {
      if (!businessId) return [];

      // Simplified query - let RLS policies handle all filtering
      const { data: reviews, error } = await supabase
        .from('reviews')
        .select(`
          *,
          review_replies(
            id,
            business_name,
            content,
            status,
            created_at,
            updated_at
          )
        `)
        .eq('business_id', businessId)
        .order('created_at', { ascending: false });

      if (error) {
        throw new Error(error.message);
      }

      const reviewsWithAuthors = await Promise.all(reviews.map(async (review) => {
        const displayName = review.display_name || 'Anonymous User';
        return {
          ...review,
          authorName: displayName,
          authorImage: `https://api.dicebear.com/7.x/avataaars/svg?seed=${review.user_id}`,
          status: review.status as 'Pending Validation' | 'Published' | 'Rejected',
          reply: review.review_replies?.[0] || null
        } as Review & { reply: any };
      }));

      return reviewsWithAuthors;
    },
    enabled: !!businessId,
  });

  const userReviewQuery = useQuery({
    queryKey: ['userReview', businessId, user?.id],
    queryFn: async () => {
      if (!businessId || !user) return null;

      const { data, error } = await supabase
        .from('reviews')
        .select('*')
        .eq('business_id', businessId)
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        throw new Error(error.message);
      }

      if (data) {
        const displayName = data.display_name || 'Anonymous User';
        return {
          ...data,
          authorName: displayName,
          status: data.status as 'Pending Validation' | 'Published' | 'Rejected'
        } as Review;
      }

      return data;
    },
    enabled: !!businessId && !!user,
  });

  const deleteReview = useMutation({
    mutationFn: async (reviewId: string) => {
      if (!user) {
        throw new Error('You must be logged in to delete a review');
      }

      const canManageAll = await isAllowedTo('manage_all_reviews');
      const isOwnReview = userReviewQuery.data?.id === reviewId;
      const canDeleteOwn = await isAllowedTo('delete_own_reviews');
      
      if (!canManageAll && !(isOwnReview && canDeleteOwn)) {
        throw new Error('You do not have permission to delete this review');
      }

      const { data, error } = await supabase
        .from('reviews')
        .delete()
        .eq('id', reviewId)
        .select();

      if (error) {
        throw new Error(error.message || 'Failed to delete review');
      }

      return reviewId;
    },
    onSuccess: (reviewId) => {
      queryClient.invalidateQueries({ queryKey: ['reviews', businessId] });
      queryClient.invalidateQueries({ queryKey: ['userReview', businessId, user?.id] });
      toast({
        title: 'Review deleted',
        description: 'Your review has been successfully deleted.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete review',
        variant: 'destructive',
      });
    },
  });

  const createReview = useMutation({
    mutationFn: async (review: { businessId: string; rating: number; description: string }) => {
      if (!user) {
        throw new Error('You must be logged in to leave a review');
      }

      const canCreateReviews = await isAllowedTo('create_reviews');
      if (!canCreateReviews) {
        throw new Error('You do not have permission to create reviews');
      }

      const isOwner = isBusinessOwnerQuery.data;
      if (isOwner) {
        throw new Error('You cannot review your own business');
      }

      const existingReview = userReviewQuery.data;
      if (existingReview) {
        throw new Error('You have already reviewed this business');
      }

      const reviewData = {
        business_id: review.businessId,
        user_id: user.id,
        rating: review.rating,
        description: review.description,
        status: 'Pending Validation' as const
      };

      const { data, error } = await supabase
        .from('reviews')
        .insert(reviewData)
        .select()
        .single();

      if (error) {
        if (error.code === '42501') {
          throw new Error('Permission denied: Cannot create review due to security policy. Please ensure you are logged in and not the business owner.');
        }
        throw new Error(error.message || 'Failed to create review');
      }

      return {
        ...data,
        status: data.status as 'Pending Validation' | 'Published' | 'Rejected'
      } as Review;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userReview', businessId, user?.id] });
      queryClient.invalidateQueries({ queryKey: ['reviews', businessId] });
      toast({
        title: 'Review submitted',
        description: 'Your review has been submitted and is pending approval.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to submit review',
        variant: 'destructive',
      });
    },
  });

  const updateReview = useMutation({
    mutationFn: async (review: { id: string; rating: number; description: string }) => {
      if (!user) {
        throw new Error('You must be logged in to update a review');
      }

      const { data, error } = await supabase
        .from('reviews')
        .update({
          rating: review.rating,
          description: review.description
        })
        .eq('id', review.id)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) {
        throw new Error(error.message || 'Failed to update review');
      }

      return {
        ...data,
        status: data.status as 'Pending Validation' | 'Published' | 'Rejected'
      } as Review;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userReview', businessId, user?.id] });
      queryClient.invalidateQueries({ queryKey: ['reviews', businessId] });
      toast({
        title: 'Review updated',
        description: 'Your review has been updated and is pending approval.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update review',
        variant: 'destructive',
      });
    },
  });

  const validateReview = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: 'Published' | 'Rejected' }) => {
      if (!user) {
        throw new Error('You must be logged in to validate a review');
      }

      const canValidate = await isAllowedTo('view_all_reviews');
      if (!canValidate) {
        throw new Error('You do not have permission to validate reviews');
      }

      const { data, error } = await supabase
        .from('reviews')
        .update({ status })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        throw new Error(error.message || 'Failed to validate review');
      }

      return {
        ...data,
        status: data.status as 'Pending Validation' | 'Published' | 'Rejected'
      } as Review;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reviews', businessId] });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to validate review',
        variant: 'destructive',
      });
    },
  });

  // Calculate metrics from the reviews returned by RLS policies
  const allVisibleReviews = reviewsQuery.data || [];
  const publishedReviews = allVisibleReviews.filter(review => review.status === 'Published');
  
  // Use published reviews for public metrics (rating/count)
  const averageRating = publishedReviews.length > 0
    ? publishedReviews.reduce((sum, review) => sum + review.rating, 0) / publishedReviews.length
    : undefined;

  return {
    reviews: allVisibleReviews,
    isLoading: reviewsQuery.isLoading,
    error: reviewsQuery.error,
    userReview: userReviewQuery?.data,
    isUserReviewLoading: userReviewQuery?.isLoading,
    userReviewError: userReviewQuery?.error,
    isBusinessOwner: isBusinessOwnerQuery.data || false,
    createReview,
    updateReview,
    deleteReview,
    validateReview,
    averageRating,
    reviewCount: publishedReviews.length
  };
};
